import {
  buildGraphId,
  isTaskGraphView,
  parseGraphId
} from '@/lib/graph-identifier';
import {
  type CRNodeData,
  NODE_HEIGHT,
  NODE_WIDTH,
  activeTreeNodeIds,
  buildReactFlowGraph,
  collectDescendants,
  computeFullyCompleteParents,
  computeInactiveParents,
  computeNonActiveTargetRoots,
  computeStixHead,
  graphStructureSignature
} from '@/lib/graph-layout';
import { cn } from '@/lib/utils';
import {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow
} from '@xyflow/react';
import { MoveHorizontal, MoveVertical, Network, Play, X } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  type GraphProps,
  type IGraph,
  type IStep,
  type NodeInfo,
  graphState,
  sessionState,
  useChatInteract,
  useChatMessages,
  userState
} from '@chainlit/react-client';

import GraphTimeline from '@/components/GraphTimeline';
import { useTheme } from '@/components/ThemeProvider';
import CRNode from '@/components/graph/CRNode';
import SnapshotTimeline from '@/components/rewind/SnapshotTimeline';
import { Button } from '@/components/ui/button';
import { CardContent, CardSquare } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

import { useIsMobile } from '@/hooks/use-mobile';

import {
  GraphSnapshot,
  graphHistoryState,
  snapshotContentKey
} from '@/state/graphHistory';
import {
  activeTargetIdState,
  graphSelectionState,
  selectedSnapshotIndexState
} from '@/state/graphSelection';

import '@xyflow/react/dist/style.css';

const nodeTypes = { crNode: CRNode };

function GraphInner({ className }: GraphProps) {
  const { variant } = useTheme();
  const graphs = useRecoilValue<Record<string, IGraph>>(graphState);
  const setGraphs = useSetRecoilState(graphState);
  const [selectedNode, setSelectedNode] = useState<NodeInfo | null>(null);
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
  const [selectedGraphId, setSelectedGraphId] =
    useRecoilState(graphSelectionState);
  const [activeTargetId, setActiveTargetId] =
    useRecoilState(activeTargetIdState);
  const { threadId } = useChatMessages();
  const lastThreadIdRef = useRef<string | undefined>(threadId);
  const [graphHistory, setGraphHistory] = useRecoilState(graphHistoryState);
  const [selectedSnapshotIndex, setSelectedSnapshotIndex] = useRecoilState(
    selectedSnapshotIndexState
  );
  const [pendingRunTaskNodeId, setPendingRunTaskNodeId] = useState<
    string | null
  >(null);
  const pendingRunTaskBaselineRef = useRef<number>(0);
  const userExpandedRef = useRef<Set<string>>(new Set());
  const autoCollapsedRef = useRef<Set<string>>(new Set());
  const centeredSelectionRef = useRef<string | null>(null);
  const manualCollapseRef = useRef(false);
  const [autoFollow, setAutoFollow] = useState<boolean>(() => {
    try {
      return localStorage.getItem('cr-graph-auto-follow') === '1';
    } catch {
      return false;
    }
  });
  const [layoutDirection, setLayoutDirection] = useState<'TB' | 'LR'>(() => {
    try {
      return localStorage.getItem('cr-graph-direction') === 'LR' ? 'LR' : 'TB';
    } catch {
      return 'TB';
    }
  });
  const session = useRecoilValue(sessionState);
  const user = useRecoilValue(userState);
  const { sendMessage } = useChatInteract();
  const { setCenter, fitBounds } = useReactFlow();
  const isMobile = useIsMobile();
  // Keep setGraphs in closure so any future reset logic lives here. Suppress
  // unused-variable lint noise without changing the public hook shape.
  void setGraphs;

  const availableGraphs = useMemo(() => graphs, [graphs]);

  const DEFAULT_GRAPH_IDENTIFIER = 'Operation Overview';

  const timelineSnapshots: GraphSnapshot[] = useMemo(
    () =>
      selectedGraphId === 'none' ? [] : (graphHistory[selectedGraphId] ?? []),
    [graphHistory, selectedGraphId]
  );
  const timelineEnabled = timelineSnapshots.length >= 2;

  const currentGraph = useMemo(() => {
    if (selectedGraphId === 'none' || !availableGraphs[selectedGraphId]) {
      return undefined;
    }
    const live = availableGraphs[selectedGraphId];
    if (
      timelineEnabled &&
      selectedSnapshotIndex !== null &&
      selectedSnapshotIndex >= 0 &&
      selectedSnapshotIndex < timelineSnapshots.length - 1
    ) {
      const snap = timelineSnapshots[selectedSnapshotIndex];
      return {
        ...live,
        nodes: snap.nodes,
        edges: snap.edges
      } as IGraph;
    }
    return live;
  }, [
    selectedGraphId,
    availableGraphs,
    timelineEnabled,
    selectedSnapshotIndex,
    timelineSnapshots
  ]);

  useEffect(() => {
    setSelectedGraphId('none');
    setSelectedSnapshotIndex(null);
    lastThreadIdRef.current = threadId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastThreadIdRef.current !== threadId) {
      lastThreadIdRef.current = threadId;
      setSelectedGraphId('none');
      setGraphHistory({});
      setSelectedSnapshotIndex(null);
      setCollapsedNodes(new Set());
      setPendingRunTaskNodeId(null);
      pendingRunTaskBaselineRef.current = 0;
      userExpandedRef.current = new Set();
      autoCollapsedRef.current = new Set();
      pendingUpgradeRef.current = true;
    }
  }, [threadId, setSelectedGraphId, setGraphHistory]);

  useEffect(() => {
    const socket = session?.socket;
    if (!socket) return;

    const handleHistory = (data: {
      identifier?: string;
      snapshots?: GraphSnapshot[];
    }) => {
      const ident = data?.identifier;
      const snapshots = data?.snapshots;
      if (!ident || !Array.isArray(snapshots) || snapshots.length === 0) return;
      setGraphHistory((prev) => ({ ...prev, [ident]: snapshots }));
    };

    const handleLiveGraph = (event: 'create' | 'update') => (data: any) => {
      const ident = data?.identifier || data?.id || data?.name;
      if (!ident) return;
      const snapshot: GraphSnapshot = {
        event,
        recorded_at: new Date().toISOString(),
        identifier: ident,
        nodes: data?.nodes ?? [],
        edges: data?.edges ?? [],
        ...data
      };
      setGraphHistory((prev) => {
        const existing = prev[ident] ?? [];
        const last = existing[existing.length - 1];
        if (
          last &&
          snapshotContentKey(last.nodes, last.edges) ===
            snapshotContentKey(snapshot.nodes, snapshot.edges)
        ) {
          // Identical to the latest timeline point — don't grow the array
          // (a resume re-emit or an unchanged re-render must not shift the
          // snapshot-vs-live boundary).
          return prev;
        }
        return { ...prev, [ident]: [...existing, snapshot] };
      });
    };

    const createHandler = handleLiveGraph('create');
    const updateHandler = handleLiveGraph('update');

    const handleActiveTarget = (data: { active_target_id?: string | null }) => {
      setActiveTargetId(data?.active_target_id ?? null);
    };

    socket.on('graph_history', handleHistory);
    socket.on('graph_create', createHandler);
    socket.on('graph_update', updateHandler);
    socket.on('active_target_changed', handleActiveTarget);
    return () => {
      socket.off('graph_history', handleHistory);
      socket.off('graph_create', createHandler);
      socket.off('graph_update', updateHandler);
      socket.off('active_target_changed', handleActiveTarget);
    };
  }, [session?.socket, setGraphHistory, setActiveTargetId]);

  const pendingUpgradeRef = useRef(true);

  useEffect(() => {
    const availableIds = Object.keys(availableGraphs);
    if (availableIds.length === 0) {
      if (selectedGraphId !== 'none') {
        setSelectedGraphId('none');
      }
      pendingUpgradeRef.current = true;
      return;
    }
    const stale =
      selectedGraphId === 'none' || !availableGraphs[selectedGraphId];
    const operationAvailable = availableIds.includes(DEFAULT_GRAPH_IDENTIFIER);
    if (stale) {
      const preferred = operationAvailable
        ? DEFAULT_GRAPH_IDENTIFIER
        : availableIds[0];
      setSelectedGraphId(preferred);
      if (preferred === DEFAULT_GRAPH_IDENTIFIER) {
        pendingUpgradeRef.current = false;
      }
    } else if (
      operationAvailable &&
      selectedGraphId !== DEFAULT_GRAPH_IDENTIFIER &&
      pendingUpgradeRef.current
    ) {
      setSelectedGraphId(DEFAULT_GRAPH_IDENTIFIER);
      pendingUpgradeRef.current = false;
    }
  }, [availableGraphs, selectedGraphId, setSelectedGraphId]);

  useEffect(() => {
    if (!autoFollow || !activeTargetId) return;
    const parsed = parseGraphId(selectedGraphId);
    if (!parsed?.target_id || parsed.target_id === activeTargetId) return;
    const next = buildGraphId(activeTargetId, parsed.view);
    if (availableGraphs[next]) setSelectedGraphId(next);
  }, [
    activeTargetId,
    availableGraphs,
    autoFollow,
    selectedGraphId,
    setSelectedGraphId
  ]);

  useEffect(() => {
    setSelectedSnapshotIndex(null);
    setSelectedNode(null);
    setPendingRunTaskNodeId(null);
    pendingRunTaskBaselineRef.current = 0;
    userExpandedRef.current = new Set();
    autoCollapsedRef.current = new Set();
    centeredSelectionRef.current = null;
  }, [selectedGraphId]);

  useEffect(() => {
    if (!pendingRunTaskNodeId) return;
    if (timelineSnapshots.length > pendingRunTaskBaselineRef.current) {
      setPendingRunTaskNodeId(null);
    }
  }, [timelineSnapshots.length, pendingRunTaskNodeId]);

  useEffect(() => {
    try {
      localStorage.setItem('cr-graph-auto-follow', autoFollow ? '1' : '0');
    } catch {
      /* localStorage unavailable */
    }
  }, [autoFollow]);

  useEffect(() => {
    try {
      localStorage.setItem('cr-graph-direction', layoutDirection);
    } catch {
      /* localStorage unavailable */
    }
  }, [layoutDirection]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, string[]>();
    const nodes = currentGraph?.nodes ?? [];
    const ids = new Set(nodes.map((n) => n.identifier));
    for (const edge of currentGraph?.edges ?? []) {
      if (!edge.source || !edge.target) continue;
      if (!ids.has(edge.source) || !ids.has(edge.target)) continue;
      if (!map.has(edge.source)) map.set(edge.source, []);
      map.get(edge.source)!.push(edge.target);
    }
    return map;
  }, [currentGraph?.nodes, currentGraph?.edges]);

  const parentsByChild = useMemo(() => {
    const map = new Map<string, string[]>();
    const nodes = currentGraph?.nodes ?? [];
    const ids = new Set(nodes.map((n) => n.identifier));
    for (const edge of currentGraph?.edges ?? []) {
      if (!edge.source || !edge.target) continue;
      if (!ids.has(edge.source) || !ids.has(edge.target)) continue;
      if (!map.has(edge.target)) map.set(edge.target, []);
      map.get(edge.target)!.push(edge.source);
    }
    return map;
  }, [currentGraph?.nodes, currentGraph?.edges]);

  const activeTaskNodeId = useMemo(() => {
    const nodes = currentGraph?.nodes ?? [];
    if (nodes.length === 0) return null;

    // Distance from a root via edge adjacency (COA graphs are trees), computed
    // with a first-visit-wins BFS so it is cycle-safe. This makes the follow
    // target DETERMINISTIC — always the same bottom-most (deepest) IN PROGRESS
    // node — instead of depending on node array order (#1128).
    const incoming = new Set<string>();
    for (const kids of childrenByParent.values()) {
      for (const k of kids) incoming.add(k);
    }
    const depth = new Map<string, number>();
    const queue = nodes
      .filter((n) => !incoming.has(n.identifier))
      .map((n) => ({ id: n.identifier, d: 0 }));
    while (queue.length) {
      const { id, d } = queue.shift()!;
      if (depth.has(id)) continue;
      depth.set(id, d);
      for (const c of childrenByParent.get(id) ?? []) {
        queue.push({ id: c, d: d + 1 });
      }
    }

    const inProgress = nodes.filter((n) => {
      const s = (n.data as Record<string, unknown> | undefined)?.status;
      return typeof s === 'string' && s.toUpperCase() === 'IN PROGRESS';
    });
    if (inProgress.length === 0) return null;

    // Deepest wins; ties broken by on_active_path (prefer the active spine) then
    // by identifier — a total order, so the result never flip-flops.
    let best = inProgress[0];
    for (const n of inProgress) {
      if (n === best) continue;
      const dn = depth.get(n.identifier) ?? 0;
      const db = depth.get(best.identifier) ?? 0;
      if (dn !== db) {
        if (dn > db) best = n;
        continue;
      }
      const an =
        (n.data as Record<string, unknown> | undefined)?.on_active_path ===
        true;
      const ab =
        (best.data as Record<string, unknown> | undefined)?.on_active_path ===
        true;
      if (an !== ab) {
        if (an) best = n;
        continue;
      }
      if (n.identifier < best.identifier) best = n;
    }
    return best.identifier;
  }, [currentGraph?.nodes, childrenByParent]);

  const followSpine = useMemo(() => {
    const spine = new Set<string>();
    if (!autoFollow || !activeTaskNodeId) return spine;
    spine.add(activeTaskNodeId);
    const queue = [activeTaskNodeId];
    while (queue.length) {
      const cur = queue.shift()!;
      for (const p of parentsByChild.get(cur) ?? []) {
        if (!spine.has(p)) {
          spine.add(p);
          queue.push(p);
        }
      }
    }
    return spine;
  }, [autoFollow, activeTaskNodeId, parentsByChild]);

  const layoutSignature = useMemo(() => {
    const nodes = currentGraph?.nodes ?? [];
    const active = new Set<string>();
    const content: string[] = [];
    nodes.forEach((n) => {
      const d = (n.data as Record<string, unknown> | undefined) ?? {};
      if (d.on_active_path === true) active.add(n.identifier);
      // Presentation that buildReactFlowGraph bakes into the node data + edge
      // colors — included so a status/risk/label change (a live update OR a
      // timeline scrub between two same-shape snapshots) actually re-renders,
      // while an unrelated React re-render with identical content still skips
      // the dagre relayout. Backend emits are batched (once per turn), so
      // reacting to content here is not the per-tick thrash #1128 removed.
      const risk = d['Risk Score'] ?? d.risk ?? '';
      content.push(
        `${n.identifier}=${d.status ?? ''}|${risk}|${n.name ?? ''}|${n.color ?? ''}`
      );
    });
    const structural = graphStructureSignature(
      nodes,
      currentGraph?.edges ?? [],
      collapsedNodes,
      active,
      layoutDirection,
      selectedGraphId,
      pendingRunTaskNodeId
    );
    return `${structural}#${content.sort().join(',')}`;
  }, [
    currentGraph?.nodes,
    currentGraph?.edges,
    collapsedNodes,
    layoutDirection,
    selectedGraphId,
    pendingRunTaskNodeId
  ]);

  const baseLayout = useMemo(
    () =>
      buildReactFlowGraph(
        currentGraph?.nodes ?? [],
        currentGraph?.edges ?? [],
        collapsedNodes,
        selectedGraphId,
        layoutDirection,
        pendingRunTaskNodeId
      ),
    // Keyed on the structural signature so a status-only tick doesn't relayout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutSignature]
  );

  useEffect(() => {
    if (!autoFollow || !activeTaskNodeId) return;
    // A manual expand/collapse (handleNodeContextMenu) also changes collapsedNodes,
    // which is a dependency below — but the operator navigating the tree by hand
    // should never yank the viewport back to the active node, even if the toggled
    // node sits on the active node's followSpine. Bail once, agent-driven changes
    // (the auto-collapse reconciliation effect, activeTaskNodeId advancing) never
    // touch this ref so they fall through to the snap logic as before (#1185).
    if (manualCollapseRef.current) {
      manualCollapseRef.current = false;
      return;
    }
    // Reveal, then center, the bottom-most in-progress node. If any node on its
    // spine (`followSpine` = the target + its real ancestors) is collapsed,
    // expand those first — a state change that re-runs this effect after the
    // relayout — so auto-follow always drills down to the deterministic
    // bottom-most in-progress node instead of stalling on a hidden one. The
    // inactive-collapse pass is taught to leave `followSpine` open (below), so
    // this expand is stable — no collapse fight, no loop. (#1128)
    const collapsedOnSpine = Array.from(followSpine).filter((id) =>
      collapsedNodes.has(id)
    );
    if (collapsedOnSpine.length > 0) {
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        collapsedOnSpine.forEach((id) => {
          next.delete(id);
          autoCollapsedRef.current.delete(id);
        });
        return next;
      });
      return;
    }
    // Center on the active node's KNOWN dagre coordinates (position is the
    // top-left, offset by half the node size to reach center) rather than
    // fitView({nodes:[{id}]}), which reads React Flow's internal store and can
    // fire before the new positions commit — the "jumps to a random spot" bug.
    const target = baseLayout.nodes.find((n) => n.id === activeTaskNodeId);
    if (!target) return;
    const raf = requestAnimationFrame(() => {
      setCenter(
        target.position.x + NODE_WIDTH / 2,
        target.position.y + NODE_HEIGHT / 2,
        { zoom: 1.2, duration: 500 }
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [
    autoFollow,
    activeTaskNodeId,
    baseLayout,
    collapsedNodes,
    followSpine,
    selectedSnapshotIndex,
    setCenter
  ]);

  const selectedIdentifier = selectedNode?.identifier ?? null;
  const rfNodes = useMemo(
    () =>
      baseLayout.nodes.map((n) => {
        const shouldSelect = n.id === selectedIdentifier;
        if (n.data.isSelected === shouldSelect) return n;
        return { ...n, data: { ...n.data, isSelected: shouldSelect } };
      }),
    [baseLayout.nodes, selectedIdentifier]
  );
  const rfEdges = baseLayout.edges;

  const handleNodeClick = useCallback(
    (_e: React.MouseEvent, node: { id: string; data: CRNodeData }) => {
      setSelectedNode({
        name: node.data.label,
        data: node.data.raw as Record<string, any>,
        identifier: node.id
      });
    },
    []
  );

  const handleNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: { id: string; data: CRNodeData }) => {
      e.preventDefault();
      const children = childrenByParent.get(node.id) ?? [];
      if (children.length === 0) return;
      const recursive = e.shiftKey;
      manualCollapseRef.current = true;
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        const willCollapse = !next.has(node.id);
        const rememberExpand = (id: string) => {
          if (autoCollapsedRef.current.has(id)) {
            userExpandedRef.current.add(id);
          }
        };
        if (!recursive) {
          if (willCollapse) next.add(node.id);
          else {
            next.delete(node.id);
            rememberExpand(node.id);
          }
          return next;
        }
        for (const current of collectDescendants(node.id, childrenByParent)) {
          if (willCollapse) next.add(current);
          else {
            next.delete(current);
            rememberExpand(current);
          }
        }
        return next;
      });
    },
    [childrenByParent]
  );

  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const parsedSelection = useMemo(
    () => parseGraphId(selectedGraphId),
    [selectedGraphId]
  );
  const isTaskGraph = parsedSelection
    ? isTaskGraphView(parsedSelection.view)
    : false;
  const isStixView = parsedSelection?.view === 'STIX';

  const fullyCompleteParents = useMemo(() => {
    if (!isTaskGraph) return new Set<string>();
    return computeFullyCompleteParents(
      currentGraph?.nodes ?? [],
      childrenByParent
    );
  }, [currentGraph?.nodes, childrenByParent, isTaskGraph]);

  const inactiveParents = useMemo(() => {
    if (!isTaskGraph) return new Set<string>();
    return computeInactiveParents(currentGraph?.nodes ?? [], childrenByParent);
  }, [currentGraph?.nodes, childrenByParent, isTaskGraph]);

  const stixHead = useMemo(() => {
    if (!isStixView) return new Set<string>();
    return computeStixHead(currentGraph?.nodes ?? [], childrenByParent);
  }, [currentGraph?.nodes, childrenByParent, isStixView]);

  // On the merged all-targets view, collapse every NON-active target down to its
  // root so only the active target's tree stays expanded. Gated to the merged
  // selection (target_id === null) — a per-target `coa:<tid>` view shows a single
  // tree and has no "other" targets to fold.
  const nonActiveTargetRoots = useMemo(() => {
    if (!isTaskGraph || parsedSelection?.target_id) return new Set<string>();
    return computeNonActiveTargetRoots(
      currentGraph?.nodes ?? [],
      activeTargetId
    );
  }, [
    currentGraph?.nodes,
    isTaskGraph,
    parsedSelection?.target_id,
    activeTargetId
  ]);

  // Center the viewport on the selected/active tree once its layout lands (auto-follow OFF only;
  // the auto-follow effect centers when ON). Computes the bounding box from known dagre coords in
  // `baseLayout.nodes` and calls `fitBounds` — NOT `fitView({nodes})`, which reads React Flow's
  // store before positions commit and lands top-left (#1128). Centers once per selection so it never
  // yanks the view back after the operator pans. Merged all-targets view → the active target's tree;
  // a per-target view → its single tree.
  useEffect(() => {
    if (autoFollow) return;
    if (centeredSelectionRef.current === selectedGraphId) return;
    const nodes = baseLayout.nodes;
    if (!nodes.length) return;
    let centerNodes = nodes;
    if (parsedSelection?.target_id == null && activeTargetId) {
      const ids = activeTreeNodeIds(
        currentGraph?.nodes ?? [],
        activeTargetId,
        childrenByParent
      );
      if (ids.size) centerNodes = nodes.filter((n) => ids.has(n.id));
    }
    if (!centerNodes.length) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of centerNodes) {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + NODE_WIDTH);
      maxY = Math.max(maxY, n.position.y + NODE_HEIGHT);
    }
    const bounds = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
    const raf = requestAnimationFrame(() => {
      fitBounds(bounds, { padding: 0.2, duration: 500 });
      centeredSelectionRef.current = selectedGraphId;
    });
    return () => cancelAnimationFrame(raf);
  }, [
    autoFollow,
    baseLayout,
    selectedGraphId,
    activeTargetId,
    parsedSelection?.target_id,
    currentGraph?.nodes,
    childrenByParent,
    fitBounds
  ]);

  // Union of both auto-collapse passes, reconciled in one effect below. Computing
  // the desired set up front (rather than each pass mutating collapsedNodes in
  // its own effect) avoids a same-commit race where the inactive pass's
  // bidirectional re-expand could undo a collapse the completeness pass just
  // applied a moment earlier (a node can finish and still carry a stale
  // on_active_path=true for one tick before the backend advances past it).
  const desiredAutoCollapsed = useMemo(() => {
    const s = new Set<string>();
    fullyCompleteParents.forEach((id) => s.add(id));
    inactiveParents.forEach((id) => {
      if (!followSpine.has(id)) s.add(id); // never fold the auto-follow spine
    });
    stixHead.forEach((id) => s.add(id));
    nonActiveTargetRoots.forEach((id) => s.add(id)); // fold non-active targets to their root
    return s;
  }, [
    fullyCompleteParents,
    inactiveParents,
    followSpine,
    stixHead,
    nonActiveTargetRoots
  ]);

  useEffect(() => {
    if (!isTaskGraph && !isStixView) return;
    setCollapsedNodes((prev) => {
      let changed = false;
      const next = new Set(prev);
      desiredAutoCollapsed.forEach((id) => {
        if (userExpandedRef.current.has(id)) return;
        if (next.has(id)) return;
        next.add(id);
        changed = true;
      });
      autoCollapsedRef.current.forEach((id) => {
        if (desiredAutoCollapsed.has(id)) return;
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      autoCollapsedRef.current = new Set(desiredAutoCollapsed);
      return changed ? next : prev;
    });
  }, [desiredAutoCollapsed, isTaskGraph, isStixView]);

  const taskDescription =
    selectedNode && typeof selectedNode.data?.description === 'string'
      ? selectedNode.data.description
      : '';
  const isStixNode = useMemo(() => {
    if (!selectedNode) return false;
    if (parsedSelection?.view === 'STIX') return true;
    const d = selectedNode.data as Record<string, unknown> | undefined;
    if (!d) return false;
    if (typeof d.item_type === 'string') return true;
    if (typeof d.type === 'string') return true;
    if (d.shared === true) return true;
    return false;
  }, [selectedNode, parsedSelection?.view]);
  const canRunTask =
    isTaskGraph &&
    !!selectedNode &&
    !isStixNode &&
    taskDescription.trim().length > 0;

  const nodeTargetId =
    (selectedNode?.data && (selectedNode.data as any).target_id) ||
    (selectedNode?.identifier && selectedNode.identifier.includes(':')
      ? selectedNode.identifier.split(':', 1)[0]
      : null);

  const handleRunTask = useCallback(() => {
    if (!canRunTask || !selectedNode) return;
    const targetLine = nodeTargetId ? `Target: ${nodeTargetId}\n` : '';
    const needsSwitch = !!nodeTargetId && nodeTargetId !== activeTargetId;
    const switchLine = needsSwitch ? `Switch to target: ${nodeTargetId}\n` : '';
    const text = `${switchLine}Run task: ${selectedNode.name}\n${targetLine}${taskDescription}`;
    const message: IStep = {
      threadId: '',
      id: uuidv4(),
      name: user?.identifier || 'User',
      type: 'user_message',
      output: text,
      createdAt: new Date().toISOString(),
      metadata: {
        location: window.location.href,
        source: 'graph_run_task',
        ...(nodeTargetId ? { target_id: nodeTargetId } : {}),
        ...(needsSwitch ? { switch_active_target: true } : {})
      }
    };
    pendingRunTaskBaselineRef.current = timelineSnapshots.length;
    setPendingRunTaskNodeId(selectedNode.identifier);
    sendMessage(message, []);
    setSelectedNode(null);
  }, [
    canRunTask,
    selectedNode,
    taskDescription,
    nodeTargetId,
    activeTargetId,
    sendMessage,
    user?.identifier,
    timelineSnapshots.length
  ]);

  const runTaskTooltip = !selectedNode
    ? 'Select a node first'
    : !isTaskGraph
      ? 'Run Task is available on COA / Operation graphs'
      : !canRunTask
        ? 'This node has no runnable description'
        : nodeTargetId
          ? `Send this task to the agent (target ${nodeTargetId})`
          : 'Send this task to the agent';

  const showSwitchTarget =
    !!nodeTargetId &&
    nodeTargetId !== (activeTargetId ?? parsedSelection?.target_id);

  const handleSwitchTarget = useCallback(() => {
    if (!nodeTargetId) return;
    const text = `Switch to target: ${nodeTargetId}`;
    const message: IStep = {
      threadId: '',
      id: uuidv4(),
      name: user?.identifier || 'User',
      type: 'user_message',
      output: text,
      createdAt: new Date().toISOString(),
      metadata: {
        location: window.location.href,
        source: 'graph_switch_target',
        target_id: nodeTargetId
      }
    };
    sendMessage(message, []);
  }, [nodeTargetId, sendMessage, user?.identifier]);

  const tokenStyles = {
    card: 'bg-card border-border',
    content: 'bg-card border-border',
    title: 'text-foreground',
    subtitle: 'text-muted-foreground',
    nodeDetails: 'bg-muted/60',
    nodeDetailsText: 'text-foreground',
    nodeDetailsValue: 'text-foreground',
    nodeDetailsId: 'text-muted-foreground',
    nodeDetailsData: 'text-muted-foreground',
    nodeDetailsBorder: 'border-border',
    button:
      'bg-muted/60 border-border text-muted-foreground hover:bg-accent hover:text-foreground',
    buttonExecute:
      'bg-primary/15 border-primary/30 text-primary hover:bg-primary/25 hover:text-primary disabled:opacity-50',
    tooltip: 'bg-popover border-border text-popover-foreground',
    loadingState: 'bg-card',
    noGraphState: 'bg-card',
    loadingIcon: 'bg-primary/15 border-primary/30',
    loadingIconColor: 'text-primary',
    loadingText: 'text-muted-foreground',
    noGraphIcon: 'bg-muted border-border',
    noGraphIconColor: 'text-muted-foreground',
    noGraphTitle: 'text-foreground',
    noGraphText: 'text-muted-foreground'
  };

  const hasGraphData = rfNodes.length > 0;

  const NodeDetails = selectedNode && (
    <div
      className={cn(
        'flex h-full flex-col backdrop-blur-sm',
        tokenStyles.nodeDetails
      )}
    >
      <ScrollArea className="flex-1 p-3">
        <div className="grid grid-cols-[minmax(60px,auto)_1fr] items-start gap-x-3 gap-y-2 text-xs min-w-0">
          <span
            className={cn(
              'font-semibold whitespace-nowrap',
              tokenStyles.nodeDetailsText
            )}
          >
            Name:
          </span>
          <span
            className={cn(
              'min-w-0 [overflow-wrap:anywhere]',
              tokenStyles.nodeDetailsValue
            )}
          >
            {selectedNode.name}
          </span>
          <span
            className={cn(
              'font-semibold whitespace-nowrap',
              tokenStyles.nodeDetailsText
            )}
          >
            ID:
          </span>
          <span
            className={cn(
              'min-w-0 font-mono text-[11px] [overflow-wrap:anywhere]',
              tokenStyles.nodeDetailsId
            )}
          >
            {selectedNode.identifier}
          </span>
          {Object.entries(selectedNode.data ?? {}).map(([key, value]) => (
            <Fragment key={key}>
              <span
                className={cn(
                  'font-semibold capitalize whitespace-nowrap',
                  tokenStyles.nodeDetailsText
                )}
              >
                {key}:
              </span>
              <span
                className={cn(
                  'min-w-0 [overflow-wrap:anywhere] whitespace-pre-wrap',
                  tokenStyles.nodeDetailsData
                )}
              >
                {typeof value === 'object'
                  ? JSON.stringify(value, null, 2)
                  : String(value)}
              </span>
            </Fragment>
          ))}
        </div>
      </ScrollArea>
      <div
        className={cn(
          'flex-none border-t backdrop-blur-sm p-2.5',
          tokenStyles.nodeDetails,
          tokenStyles.nodeDetailsBorder
        )}
      >
        <div className="flex flex-col gap-2">
          {showSwitchTarget && (
            <Button
              variant="outline"
              onClick={handleSwitchTarget}
              className={cn('w-full gap-2', tokenStyles.button)}
              title={`Switch active target to ${nodeTargetId}`}
            >
              <Network className="h-3.5 w-3.5" />
              Switch to target: {nodeTargetId}
            </Button>
          )}
          <div className="flex gap-2">
            {!isStixNode && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex-1">
                      <Button
                        onClick={handleRunTask}
                        disabled={!canRunTask}
                        className={cn(
                          'w-full gap-2',
                          tokenStyles.buttonExecute
                        )}
                        variant="outline"
                      >
                        <Play className="h-3.5 w-3.5" />
                        Run Task
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className={tokenStyles.tooltip}>
                    <p>{runTaskTooltip}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button
              variant="outline"
              onClick={() => setSelectedNode(null)}
              className={cn(
                'flex items-center gap-2',
                tokenStyles.button,
                isStixNode && 'flex-1'
              )}
            >
              <X className="h-4 w-4" />
              Hide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const FlowArea = (
    <ReactFlow
      nodes={rfNodes}
      edges={rfEdges}
      nodeTypes={nodeTypes}
      className="crg-flow"
      onNodeClick={handleNodeClick}
      onNodeContextMenu={handleNodeContextMenu}
      onPaneClick={handlePaneClick}
      fitView
      fitViewOptions={{ padding: 0.2, minZoom: 0.5, maxZoom: 1.2 }}
      defaultEdgeOptions={{
        type: 'smoothstep',
        style: { strokeWidth: 1.5 }
      }}
      nodesDraggable
      nodesConnectable={false}
      elementsSelectable
      proOptions={{ hideAttribution: true }}
      colorMode={variant === 'dark' ? 'dark' : 'light'}
      minZoom={0.2}
      maxZoom={4}
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={18}
        size={1}
        className="opacity-40"
      />
      <Controls showInteractive={false} position="bottom-right">
        <ControlButton
          onClick={() => setLayoutDirection((d) => (d === 'TB' ? 'LR' : 'TB'))}
          title={
            layoutDirection === 'TB'
              ? 'Layout: top-down — click for left-right'
              : 'Layout: left-right — click for top-down'
          }
          aria-label="Toggle graph layout direction"
        >
          {layoutDirection === 'TB' ? (
            <MoveVertical className="h-3 w-3" aria-hidden="true" />
          ) : (
            <MoveHorizontal className="h-3 w-3" aria-hidden="true" />
          )}
        </ControlButton>
        <ControlButton
          onClick={() => setAutoFollow((v) => !v)}
          title={
            autoFollow
              ? 'Auto-follow on — click to disable'
              : 'Auto-follow current task'
          }
          className={cn(autoFollow && '!bg-primary/20 !text-primary')}
          aria-pressed={autoFollow}
          aria-label="Toggle auto-follow current task"
        >
          <span className="text-[9px] font-bold leading-none tracking-tighter">
            AUTO
          </span>
        </ControlButton>
      </Controls>
    </ReactFlow>
  );

  const GraphLoadingState = (
    <div
      className={cn(
        'flex h-full w-full items-center justify-center',
        tokenStyles.loadingState
      )}
    >
      <div className="flex flex-col items-center gap-6 p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className={cn('p-4 rounded-full', tokenStyles.loadingIcon)}>
            <Network
              className={cn(
                'h-8 w-8 animate-pulse',
                tokenStyles.loadingIconColor
              )}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-primary rounded-full animate-bounce"
              style={{ animationDelay: '0.1s' }}
            ></div>
            <div
              className="w-2 h-2 bg-primary rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            ></div>
            <span
              className={cn(
                'text-sm font-medium ml-2',
                tokenStyles.loadingText
              )}
            >
              Waiting on graph data...
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const NoGraphState = (
    <div
      className={cn(
        'flex h-full w-full items-center justify-center',
        tokenStyles.noGraphState
      )}
    >
      <div className="flex flex-col items-center gap-6 p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className={cn('p-4 rounded-full', tokenStyles.noGraphIcon)}>
            <Network
              className={cn('h-12 w-12', tokenStyles.noGraphIconColor)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <h3
              className={cn('text-lg font-semibold', tokenStyles.noGraphTitle)}
            >
              No Graph Selected
            </h3>
            <p
              className={cn(
                'text-sm text-center max-w-md',
                tokenStyles.noGraphText
              )}
            >
              Select a graph from the tabs above to view its visualization
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const body =
    selectedGraphId === 'none'
      ? NoGraphState
      : hasGraphData
        ? FlowArea
        : GraphLoadingState;

  const graphAnimationStyles = (
    <style>{`
      .crg-flow .react-flow__node {
        transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
        animation: crg-node-enter 140ms ease-out both;
      }
      .crg-flow .react-flow__node.dragging,
      .crg-flow .react-flow__node.selectable:active {
        transition: none;
        animation: none;
      }
      @keyframes crg-node-enter {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      .crg-flow .react-flow__edge-path {
        transition: stroke-opacity 400ms ease-out,
                    stroke-width 300ms ease-out;
        animation: crg-edge-enter 420ms ease-out both;
      }
      @keyframes crg-edge-enter {
        from {
          stroke-opacity: 0;
          stroke-dasharray: 4 4;
        }
        to {
          stroke-opacity: 1;
          stroke-dasharray: 0;
        }
      }
      .crg-flow .react-flow__edge-textbg,
      .crg-flow .react-flow__edge-text {
        transition: opacity 300ms ease-out;
        animation: crg-fade-in 320ms ease-out both;
      }
      @keyframes crg-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `}</style>
  );

  if (isMobile) {
    return (
      <CardSquare
        className={cn(tokenStyles.card, 'flex h-full flex-col', className)}
      >
        {graphAnimationStyles}
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          <div
            className={cn(
              'relative min-h-0 flex-1 w-full shadow-inner overflow-hidden',
              tokenStyles.content
            )}
          >
            {body}
          </div>
          {timelineEnabled ? (
            <GraphTimeline
              snapshots={timelineSnapshots}
              selectedIndex={selectedSnapshotIndex}
              onSelect={setSelectedSnapshotIndex}
            />
          ) : null}
          <SnapshotTimeline />
          {selectedNode && (
            <div
              className={cn(
                'flex-none border-t backdrop-blur-sm max-h-[40vh] min-h-[200px] shadow-lg',
                tokenStyles.nodeDetails,
                tokenStyles.nodeDetailsBorder
              )}
            >
              {NodeDetails}
            </div>
          )}
        </CardContent>
      </CardSquare>
    );
  }

  return (
    <CardSquare
      className={cn(tokenStyles.card, 'flex h-full flex-col', className)}
    >
      {graphAnimationStyles}
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        <div
          className={cn(
            'relative flex-1 min-h-0 w-full shadow-inner overflow-hidden',
            tokenStyles.content
          )}
        >
          {body}
          {selectedNode && (
            <aside
              className={cn(
                'absolute right-0 top-0 bottom-0 z-20 w-[420px] max-w-[55%]',
                'border-l shadow-xl backdrop-blur-sm',
                tokenStyles.nodeDetails,
                tokenStyles.nodeDetailsBorder
              )}
            >
              {NodeDetails}
            </aside>
          )}
        </div>
        {timelineEnabled ? (
          <GraphTimeline
            snapshots={timelineSnapshots}
            selectedIndex={selectedSnapshotIndex}
            onSelect={setSelectedSnapshotIndex}
          />
        ) : null}
        <SnapshotTimeline />
      </CardContent>
    </CardSquare>
  );
}

export default function Graph(props: GraphProps) {
  return (
    <ReactFlowProvider>
      <GraphInner {...props} />
    </ReactFlowProvider>
  );
}
